"use client";

import { useRef, useEffect } from "react";
import {
  Beef,
  CakeSlice,
  Croissant,
  EggFried,
  Fish,
  Pizza,
  Salad,
  Sandwich,
  Shrimp,
  Star,
  Utensils,
  UtensilsCrossed,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getNearestVerticalScrollTop } from "@/lib/menu-scrollspy";

// Map a data-driven category (by key, then label) to a fitting lucide icon.
// Defaults to UtensilsCrossed when no keyword matches.
function categoryIcon(key: string, label: string): LucideIcon {
  const haystack = `${key} ${label}`.toLowerCase();
  if (/popular|featured|favorite|trending|best/.test(haystack)) return Star;
  if (/pizza/.test(haystack)) return Pizza;
  if (/handheld|burger|sandwich|wrap|panini|sub/.test(haystack)) return Sandwich;
  if (/salad/.test(haystack)) return Salad;
  if (/benedict|brunch_main|egg|omelet/.test(haystack)) return EggFried;
  if (/sweet|pancake|waffle|french toast|pastry|bakery|crepe/.test(haystack))
    return Croissant;
  if (/starter|appetizer|shrimp|small plate/.test(haystack)) return Shrimp;
  if (/steak|beef|burger/.test(haystack)) return Beef;
  if (/seafood|fish|salmon/.test(haystack)) return Fish;
  if (/dessert|cake|sweet treat/.test(haystack)) return CakeSlice;
  if (/pasta|side|main|entree/.test(haystack)) return Utensils;
  return UtensilsCrossed;
}

interface CategorySidebarProps {
  categories: readonly { key: string; label: string; icon?: string }[];
  activeCategory: string;
  onCategoryClick: (categoryKey: string) => void;
}

export function CategorySidebar({
  categories,
  activeCategory,
  onCategoryClick,
}: CategorySidebarProps) {
  const scrollerRef = useRef<HTMLElement>(null);
  const activeButtonRef = useRef<HTMLButtonElement>(null);

  // Keep the active button visible inside the sidebar viewport. Calling
  // scrollIntoView here also moves the document and fights the page scrollspy.
  useEffect(() => {
    const activeButton = activeButtonRef.current;
    const viewport = scrollerRef.current;
    if (!activeButton || !viewport) return;

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const itemRect = activeButton.getBoundingClientRect();
    const viewportRect = viewport.getBoundingClientRect();

    viewport.scrollTo({
      behavior: prefersReduced ? "auto" : "smooth",
      top: getNearestVerticalScrollTop({
        currentScrollTop: viewport.scrollTop,
        itemTop: itemRect.top,
        itemHeight: itemRect.height,
        containerTop: viewportRect.top,
        containerHeight: viewport.clientHeight,
        padding: 8,
      }),
    });
  }, [activeCategory]);

  return (
    <aside
      ref={scrollerRef}
      data-category-sidebar-scroller
      className="sticky top-[var(--order-sticky-offset,7rem)] hidden max-h-[calc(100vh-var(--order-sticky-offset,7rem))] w-64 flex-shrink-0 self-start overflow-y-auto overscroll-contain lg:block"
    >
      <nav aria-label="Category Navigation" className="space-y-1 pr-4">
        {categories.map((category) => {
          const isActive = category.key === activeCategory;
          const Icon = categoryIcon(category.key, category.label);
          return (
            <button
              key={category.key}
              ref={isActive ? activeButtonRef : undefined}
              aria-current={isActive ? "true" : undefined}
              onClick={() => onCategoryClick(category.key)}
              className={cn(
                "w-full rounded-lg px-3 py-2.5 text-left text-[15px] font-semibold leading-5 transition-colors",
                "hover:bg-[#f4f4f5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--roma-terracotta)] focus-visible:ring-offset-2",
                isActive
                  ? "bg-[#0d0d0d] text-white shadow-[0_1px_3px_rgb(0_0_0_/_0.16)]"
                  : "text-[#575757] hover:text-[#0d0d0d]",
              )}
            >
              <div className="flex items-center gap-2.5">
                <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <span>{category.label}</span>
              </div>
            </button>
          );
        })}
      </nav>
    </aside>
  );
}
